/**
 * Route -> translated-route map. The ONE place that knows which pages have a
 * counterpart in another language.
 *
 * Only Radar is translated today. The map exists rather than a hardcoded link
 * so the switcher can never offer a translation that does not exist: a control
 * that promises an Arabic version of the page you are on and then drops you
 * somewhere unrelated is worse than no control at all.
 *
 * URLs stay relative here on purpose — the absolute forms used for hreflang
 * live in `@/lib/seo/brand-constants` (RADAR_LANGUAGE_ALTERNATES) and are
 * consumed by page metadata. These two must describe the same cluster; when a
 * route is added to one, add it to the other.
 */

export interface LocaleAlternate {
  /** BCP-47 tag for `hrefLang` / `lang`. */
  locale: string
  /** Writing direction of the target document. */
  dir: 'ltr' | 'rtl'
  /** Path of the translated page. */
  href: string
  /** Language name in its OWN language — the convention users expect. */
  label: string
  /** Accessible name, written in the language of the page being linked TO. */
  ariaLabel: string
}

const AR_RADAR: LocaleAlternate = {
  locale: 'ar',
  dir: 'rtl',
  href: '/ar/radar',
  label: 'العربية',
  ariaLabel: 'عرض Radar باللغة العربية',
}

const EN_RADAR: LocaleAlternate = {
  locale: 'en',
  dir: 'ltr',
  href: '/platform',
  label: 'English',
  ariaLabel: 'View Radar in English',
}

const ALTERNATES_BY_PATH: Record<string, LocaleAlternate> = {
  '/platform': AR_RADAR,
  '/ar/radar': EN_RADAR,
}

/**
 * The translation of `pathname`, or null when the page has none.
 *
 * Trailing slashes are normalised; query strings and hashes are not part of a
 * pathname. Anything unmapped returns null so the switcher simply does not
 * render — silence is correct for the 99% of pages that are English-only.
 */
export function getLocaleAlternate(
  pathname: string | null
): LocaleAlternate | null {
  if (!pathname) return null
  const normalized =
    pathname.length > 1 && pathname.endsWith('/')
      ? pathname.slice(0, -1)
      : pathname
  return ALTERNATES_BY_PATH[normalized] ?? null
}
